import { useEffect, useMemo, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import API from "../api/axios";
import { hasFullAccess, getUser } from "../auth/auth";
import PageContainer from "../components/ui/PageContainer";
import DateFilter from "../components/ui/DateFilter";
import RefreshButton from "../components/ui/RefreshButton";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  MenuItem,
  TextField,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Collapse,
  Tooltip,
  Avatar,
  Divider,
} from "@mui/material";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import PeopleIcon from "@mui/icons-material/People";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StarIcon from "@mui/icons-material/Star";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LockIcon from "@mui/icons-material/Lock";
import { ACCENT, NAVY, STATUS_COLORS } from "../theme";
import LoadingState from "../components/ui/LoadingState";
import FilterBar from "../components/ui/FilterBar";
import StatusBadge from "../components/ui/StatusBadge";
import EmptyState from "../components/ui/EmptyState";
import KpiCard from "../components/ui/KpiCard";
import CandidateFunnel from "../components/ui/CandidateFunnel";
import AgingHistogram from "../components/ui/AgingHistogram";
import AttentionQueue from "../components/ui/AttentionQueue";
import RecruiterLeaderboard from "../components/ui/RecruiterLeaderboard";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend,
} from "recharts";
import logo from "../assets/logo.jpg";

// Maps the existing MUI Chip `color` names used by STATUS (success/info/
// primary/warning/error/default) to the STATUS_COLORS tone keys introduced
// in Phase 1, chosen so the rendered color is identical to before: this
// theme's palette.info.main/palette.primary.main are both already the same
// hex as STATUS_COLORS.info (#2563eb), and success/warning/error/default all
// have a matching or near-identical counterpart. This changes only which
// component renders the badge, not the underlying feedback value or color.
const MUI_COLOR_TO_TONE = {
  success: "ok",
  info: "info",
  primary: "info",
  warning: "warn",
  error: "danger",
  default: "neutral",
};

// Normalize company names so "PROFINCH" / "Profinch" / "Profinch " all match.
const normCompany = (s) => (s || "").trim().toLowerCase();

const STATUS = [
  { key: "SHORTLISTED", label: "Shortlisted", color: "success" },
  { key: "SELECTED", label: "Selected", color: "info" },
  { key: "L1_ROUND", label: "L1 Round", color: "info" },
  { key: "L2_ROUND", label: "L2 Round", color: "primary" },
  { key: "ON_HOLD", label: "On Hold", color: "warning" },
  { key: "REJECTED", label: "Rejected", color: "error" },
  { key: "DUPLICATE", label: "Duplicate", color: "default" },
  { key: "PENDING", label: "Pending", color: "default" },
];
const statusOf = (k) => STATUS.find((s) => s.key === k) || STATUS[7];

function candidateName(c) {
  const fn = [c.firstName, c.lastName].filter(Boolean).join(" ").trim();
  return fn || c.candidateName || "Unnamed";
}

// ---- Calendar-day freshness ----
// Uses positionDate if present, else createdAt. Difference is in CALENDAR days:
// same date = 0 (Fresh), next calendar date = 1 day old, etc. Time of day ignored.
function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function freshnessDays(job) {
  const raw = job.positionDate || job.createdAt;
  if (!raw) return null;
  const created = startOfDay(raw);
  const today = startOfDay(new Date());
  return Math.round((today - created) / 86400000);
}
function getFreshness(job) {
  const days = freshnessDays(job);
  if (days === null)
    return { label: "—", color: "default", group: "old", days: null };
  if (days <= 1)
    return { label: "Fresh", color: "success", group: "fresh", days };
  if (days <= 3)
    return { label: "Aging", color: "warning", group: "aging", days };
  return { label: "Old", color: "error", group: "old", days };
}

const FRESH_GROUPS = [
  {
    key: "fresh",
    label: "Fresh",
    dotColor: "success.main",
    subtitle: "≤1 day old",
  },
  {
    key: "aging",
    label: "Aging",
    dotColor: "warning.main",
    subtitle: "2-3 days old",
  },
  { key: "old", label: "Old", dotColor: "error.main", subtitle: ">3 days old" },
];

const FRESH_LEGEND = [
  { c: "success.main", l: "Fresh ≤1 day" },
  { c: "warning.main", l: "Aging 2-3 days" },
  { c: "error.main", l: "Old >3 days" },
];

function dateMode(from, to, today) {
  if (from === today && to === today) return "today";
  if (!from && !to) return "alltime";
  return "custom";
}

export default function Dashboard() {
  const fullAccess = hasFullAccess();
  const user = getUser() || {};
  const navigate = useNavigate();
  const today = new Date().toISOString().split("T")[0];

  const [jobs, setJobs] = useState([]);
  const [cands, setCands] = useState([]);
  const [users, setUsers] = useState([]);
  const [clientCompanies, setClientCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companyFilter, setCompanyFilter] = useState("");
  const [recruiterFilter, setRecruiterFilter] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [openPos, setOpenPos] = useState(null);

  const loadData = (withSpinner = false) => {
    if (withSpinner) setLoading(true);
    Promise.all([
      API.get("/jobs").then((r) => setJobs(r.data)),
      API.get("/candidates").then((r) => setCands(r.data)),
    ]).finally(() => setLoading(false));
    if (fullAccess) {
      API.get("/account/users")
        .then((r) => setUsers(r.data))
        .catch(() => {});
      API.get("/clients")
        .then((r) => setClientCompanies(r.data || []))
        .catch(() => {});
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullAccess]);

  // Auto-refresh when the user returns to this browser tab (silent)
  useEffect(() => {
    const onFocus = () => loadData();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullAccess]);

  const myName = user?.name || "";

  // Deduped company list: one entry per normalized name, clean display label.
  const companyNames = useMemo(() => {
    const map = new Map(); // normalized -> display label
    clientCompanies.forEach((c) => {
      const raw = (c.name || "").trim();
      if (raw && !map.has(normCompany(raw))) map.set(normCompany(raw), raw);
    });
    jobs.forEach((j) => {
      const raw = (j.companyName || "").trim();
      if (!raw) return;
      const key = normCompany(raw);
      if (!map.has(key)) map.set(key, raw);
    });
    return [...map.values()].sort((a, b) => a.localeCompare(b));
  }, [jobs, clientCompanies]);

  const jobCodesForCompany = useMemo(() => {
    if (!companyFilter) return null;
    return new Set(
      jobs
        .filter(
          (j) => normCompany(j.companyName) === normCompany(companyFilter),
        )
        .map((j) => j.jobCode),
    );
  }, [jobs, companyFilter]);

  const recruiterNames = useMemo(() => {
    const fromUsers = users
      .filter((u) => u.role === "RECRUITER" || u.role === "STAFF")
      .map((u) => u.name)
      .filter(Boolean);
    const fromCands = cands.map((c) => c.recruiterName).filter(Boolean);
    return [...new Set([...fromUsers, ...fromCands])].sort();
  }, [users, cands]);

  // Compare by CALENDAR DATE (local), not timestamps, so the same-day range
  // works and no record is missed due to timezone offsets.
  // If the value already starts with YYYY-MM-DD (e.g. a plain date like
  // candidateDate/positionDate, or an ISO timestamp), take those 10 chars
  // VERBATIM — never run it through new Date(), which shifts by timezone.
  const localDateStr = (value) => {
    if (!value) return null;
    const s = String(value);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;
    // Fallback for any other format: convert via local components
    const d = new Date(s);
    if (isNaN(d.getTime())) return null;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const inRange = (createdAt) => {
    if (!from && !to) return true;
    const ds = localDateStr(createdAt);
    if (!ds) return false;
    if (from && ds < from) return false; // string compare works for YYYY-MM-DD
    if (to && ds > to) return false;
    return true;
  };

  // KPI stats: respect company + recruiter + date filters, and (for staff) own data
  const statsCands = useMemo(() => {
    return cands.filter((c) => {
      if (jobCodesForCompany && !jobCodesForCompany.has(c.jobCode))
        return false;
      if (recruiterFilter && c.recruiterName !== recruiterFilter) return false;
      if (!fullAccess && c.recruiterName !== myName) return false;
      // Use the manual candidate Date (falls back to createdAt if blank)
      if (!inRange(c.candidateDate || c.createdAt)) return false;
      return true;
    });
  }, [
    cands,
    jobCodesForCompany,
    recruiterFilter,
    from,
    to,
    fullAccess,
    myName,
  ]);

  // Positions filtered by company + recruiter + date.
  // Date uses positionDate (the requirement date), falling back to createdAt
  // when positionDate is blank — so nothing silently disappears.
  const filteredJobs = useMemo(() => {
    return jobs.filter((j) => {
      if (
        companyFilter &&
        normCompany(j.companyName) !== normCompany(companyFilter)
      )
        return false;
      if (recruiterFilter) {
        const has = cands.some(
          (c) => c.jobCode === j.jobCode && c.recruiterName === recruiterFilter,
        );
        if (!has) return false;
      }
      if (!inRange(j.positionDate || j.createdAt)) return false;
      return true;
    });
  }, [jobs, cands, companyFilter, recruiterFilter, from, to]);

  // Candidates shown UNDER each position: NOT date-filtered (fixes the 0-counts bug).
  // Admin: all candidates. Staff/Recruiter: only their own. Company filter still applies.
  const candsByJob = useMemo(() => {
    const m = {};
    cands
      .filter((c) => {
        if (jobCodesForCompany && !jobCodesForCompany.has(c.jobCode))
          return false;
        if (recruiterFilter && c.recruiterName !== recruiterFilter)
          return false;
        if (!fullAccess && c.recruiterName !== myName) return false;
        return true;
      })
      .forEach((c) => {
        if (!c.jobCode) return;
        (m[c.jobCode] = m[c.jobCode] || []).push(c);
      });
    return m;
  }, [cands, jobCodesForCompany, recruiterFilter, fullAccess, myName]);

  // Positions that have NO candidates submitted yet, grouped by freshness.
  // (candsByJob is the full candidate map for visible positions.)
  const jobsByFreshness = useMemo(() => {
    const groups = { fresh: [], aging: [], old: [] };
    filteredJobs.forEach((j) => {
      const hasCandidates = (candsByJob[j.jobCode] || []).length > 0;
      if (!hasCandidates) groups[getFreshness(j).group].push(j);
    });
    return groups;
  }, [filteredJobs, candsByJob]);

  // Count of positions still awaiting candidates (for the header chip)
  const awaitingCount = useMemo(
    () =>
      jobsByFreshness.fresh.length +
      jobsByFreshness.aging.length +
      jobsByFreshness.old.length,
    [jobsByFreshness],
  );

  // Position status counts (Open = ACTIVE in DB)
  const posStatusCounts = useMemo(() => {
    const c = { OPEN: 0, ON_HOLD: 0, CLOSED: 0 };
    filteredJobs.forEach((j) => {
      const s = j.status || "ACTIVE";
      if (s === "ON_HOLD") c.ON_HOLD++;
      else if (s === "CLOSED") c.CLOSED++;
      else c.OPEN++; // ACTIVE or anything else counts as Open
    });
    return c;
  }, [filteredJobs]);

  // KPI totals: count candidates by the CANDIDATE date filter (statsCands).
  // Do NOT also require their position to be in range — a candidate added on
  // the 17th for a position dated the 16th must still count on the 17th.
  const profileCands = statsCands;

  const tot = profileCands.length;
  const cf = (k) => profileCands.filter((c) => c.feedback === k).length;
  const pct = (v) => (tot > 0 ? Math.round((v / tot) * 100) : 0);

  // ── Positions cards: Total, Open, On Hold, Closed ──
  const positionKpis = [
    {
      label: "Total Positions",
      value: filteredJobs.length,
      Icon: WorkOutlineIcon,
      color: ACCENT,
      tag: "All",
    },
    {
      label: "Open Positions",
      value: posStatusCounts.OPEN,
      Icon: LockOpenIcon,
      color: "#15803d",
      tag: "Open",
    },
    {
      label: "On Hold Positions",
      value: posStatusCounts.ON_HOLD,
      Icon: PauseCircleIcon,
      color: "#b45309",
      tag: "Hold",
    },
    {
      label: "Closed Positions",
      value: posStatusCounts.CLOSED,
      Icon: LockIcon,
      color: "#64748b",
      tag: "Closed",
    },
  ];

  // ── Candidates cards: Total Profiles, Pending, Shortlisted, L1, L2, On Hold, Selected, Rejected ──
  const candidateKpis = [
    {
      label: "Total Profiles",
      value: tot,
      Icon: PeopleIcon,
      color: "#475569",
      tag: companyFilter || recruiterFilter ? "Filtered" : "All",
    },
    {
      label: "Pending",
      value: cf("PENDING"),
      Icon: AccessTimeIcon,
      color: "#7c3aed",
      tag: pct(cf("PENDING")) + "%",
    },
    {
      label: "Shortlisted",
      value: cf("SHORTLISTED"),
      Icon: CheckCircleIcon,
      color: "#15803d",
      tag: pct(cf("SHORTLISTED")) + "%",
    },
    {
      label: "L1 Round",
      value: cf("L1_ROUND"),
      Icon: RadioButtonCheckedIcon,
      color: "#0d9488",
      tag: pct(cf("L1_ROUND")) + "%",
    },
    {
      label: "L2 Round",
      value: cf("L2_ROUND"),
      Icon: RadioButtonCheckedIcon,
      color: "#4f46e5",
      tag: pct(cf("L2_ROUND")) + "%",
    },
    {
      label: "On Hold",
      value: cf("ON_HOLD"),
      Icon: PauseCircleIcon,
      color: "#b45309",
      tag: pct(cf("ON_HOLD")) + "%",
    },
    {
      label: "Selected",
      value: cf("SELECTED"),
      Icon: StarIcon,
      color: "#1d4ed8",
      tag: pct(cf("SELECTED")) + "%",
    },
    {
      label: "Rejected",
      value: cf("REJECTED"),
      Icon: CancelIcon,
      color: "#b91c1c",
      tag: pct(cf("REJECTED")) + "%",
    },
    {
      label: "Duplicate",
      value: cf("DUPLICATE"),
      Icon: ContentCopyIcon,
      color: "#64748b",
      tag: pct(cf("DUPLICATE")) + "%",
    },
  ];

  const recruitersFor = (jobCode) => [
    ...new Set(
      (candsByJob[jobCode] || []).map((c) => c.recruiterName).filter(Boolean),
    ),
  ];

  // ── Chart data (derived from the same counts the cards use) ──
  // Donut: candidates by feedback status
  const candidateChartData = [
    { name: "Pending", value: cf("PENDING"), color: "#7c3aed" },
    { name: "Shortlisted", value: cf("SHORTLISTED"), color: "#15803d" },
    { name: "L1 Round", value: cf("L1_ROUND"), color: "#0d9488" },
    { name: "L2 Round", value: cf("L2_ROUND"), color: "#4f46e5" },
    { name: "On Hold", value: cf("ON_HOLD"), color: "#b45309" },
    { name: "Selected", value: cf("SELECTED"), color: "#1d4ed8" },
    { name: "Rejected", value: cf("REJECTED"), color: "#b91c1c" },
    { name: "Duplicate", value: cf("DUPLICATE"), color: "#64748b" },
  ].filter((d) => d.value > 0);

  // Bar: positions by status
  const positionChartData = [
    { name: "Open", value: posStatusCounts.OPEN, color: "#15803d" },
    { name: "On Hold", value: posStatusCounts.ON_HOLD, color: "#b45309" },
    { name: "Closed", value: posStatusCounts.CLOSED, color: "#64748b" },
  ];

  // ══════════════════════════════════════════════════════════════════
  // Phase 6 (Design Gap Audit item F) — prototype-style Dashboard data.
  // Everything below is derived from data already loaded above
  // (filteredJobs, statsCands/profileCands, candsByJob, jobsByFreshness) —
  // no new API calls were added for this section.
  // ══════════════════════════════════════════════════════════════════

  // -- F.1 Headline KPIs --
  // Sparklines/deltas are intentionally omitted (see Sparkline.jsx) since
  // no historical time-series data exists in the current APIs.
  const submissionsPerOpenRole =
    posStatusCounts.OPEN > 0 ? (tot / posStatusCounts.OPEN).toFixed(1) : "—";
  const interviewedCount = cf("L1_ROUND") + cf("L2_ROUND") + cf("SELECTED");
  const interviewToOfferPct =
    interviewedCount > 0
      ? Math.round((cf("SELECTED") / interviewedCount) * 100)
      : null;

  const headlineKpis = [
    { label: "Open positions", value: posStatusCounts.OPEN, color: "#15803d" },
    {
      label: "Submissions per open role",
      value: submissionsPerOpenRole,
      color: "#2563eb",
    },
    {
      label: "Interview → offer rate",
      value: interviewToOfferPct === null ? "—" : `${interviewToOfferPct}%`,
      color: "#7c3aed",
    },
    {
      label: "Roles with zero profiles",
      value: awaitingCount,
      color: "#dc2626",
      alert: true,
      actionLabel: "Work the queue →",
      onAction: () => navigate("/jobs"),
    },
  ];

  // -- F.2 Candidate funnel --
  // Caveat (see CandidateFunnel.jsx): feedback is current-status-only, so
  // this under-counts earlier stages for candidates later rejected. Best
  // available approximation from the existing data model.
  const funnelStages = [
    { label: "Profiles added", value: tot },
    {
      label: "Screened (past pending)",
      value: tot - cf("PENDING") - cf("DUPLICATE"),
    },
    {
      label: "Shortlisted+",
      value:
        cf("SHORTLISTED") +
        cf("L1_ROUND") +
        cf("L2_ROUND") +
        cf("SELECTED") +
        cf("ON_HOLD"),
    },
    { label: "Interviewed (L1/L2/Selected)", value: interviewedCount },
    { label: "Selected", value: cf("SELECTED") },
  ];

  // Real, computed purely from funnelStages above — finds the single
  // stage-to-stage transition with the lowest conversion rate. Returns
  // null (rendered as no callout) if there's nothing meaningful to compute
  // yet (fewer than 2 stages with data), rather than showing a misleading
  // insight from near-zero numbers.
  const funnelLeak = (() => {
    let worst = null;
    for (let i = 1; i < funnelStages.length; i++) {
      const prev = funnelStages[i - 1].value;
      const cur = funnelStages[i].value;
      if (prev <= 0) continue;
      const pct = Math.round((cur / prev) * 100);
      if (!worst || pct < worst.pct) {
        worst = {
          from: funnelStages[i - 1].label,
          to: funnelStages[i].label,
          pct,
        };
      }
    }
    return worst;
  })();

  // -- F.3 Ageing of open roles --
  // Uses the same freshnessDays() already used by the existing freshness
  // grouping, but with 5 finer buckets matching the prototype, computed
  // only over OPEN positions (not on-hold/closed).
  const openJobs = filteredJobs.filter(
    (j) =>
      (j.status || "ACTIVE") !== "ON_HOLD" &&
      (j.status || "ACTIVE") !== "CLOSED",
  );
  const agingBucketDefs = [
    { label: "0-1d", test: (d) => d <= 1 },
    { label: "2-3d", test: (d) => d >= 2 && d <= 3 },
    { label: "4-7d", test: (d) => d >= 4 && d <= 7 },
    { label: "8-14d", test: (d) => d >= 8 && d <= 14 },
    { label: "15d+", test: (d) => d >= 15 },
  ];
  const openJobAges = openJobs
    .map((j) => freshnessDays(j))
    .filter((d) => d !== null);
  const agingBuckets = agingBucketDefs.map((b) => ({
    label: b.label,
    count: openJobAges.filter(b.test).length,
  }));
  const sortedAges = [...openJobAges].sort((a, b) => a - b);
  const medianAge =
    sortedAges.length === 0
      ? null
      : sortedAges.length % 2 === 1
        ? sortedAges[(sortedAges.length - 1) / 2]
        : Math.round(
            (sortedAges[sortedAges.length / 2 - 1] +
              sortedAges[sortedAges.length / 2]) /
              2,
          );
  const oldestAge =
    sortedAges.length === 0 ? null : sortedAges[sortedAges.length - 1];

  // -- F.4 Needs attention --
  // Only the two reliably-derivable conditions are included (see
  // AttentionQueue.jsx for why "stalled"/"awaiting feedback"/"interview
  // today" are intentionally left out rather than fabricated).
  const slaBreachJobs = jobsByFreshness.old; // already: >3 days old, 0 candidates
  const duplicateCount = cf("DUPLICATE");
  const attentionItems = [
    ...(slaBreachJobs.length > 0
      ? [
          {
            id: "sla-breach",
            label: `${slaBreachJobs.length} position${slaBreachJobs.length !== 1 ? "s" : ""} open >3 days with no candidates`,
            description:
              slaBreachJobs
                .slice(0, 3)
                .map((j) => j.jobCode)
                .join(", ") +
              (slaBreachJobs.length > 3
                ? `, +${slaBreachJobs.length - 3} more`
                : ""),
            actionLabel: "View positions",
            onAction: () => navigate("/jobs"),
          },
        ]
      : []),
    ...(duplicateCount > 0
      ? [
          {
            id: "duplicates",
            label: `${duplicateCount} candidate${duplicateCount !== 1 ? "s" : ""} flagged as duplicate`,
            actionLabel: "Review",
            onAction: () => navigate("/candidates"),
          },
        ]
      : []),
  ];

  // -- F.5 Recruiter leaderboard --
  const leaderboardEntries = recruiterNames
    .map((name) => {
      const theirs = profileCands.filter((c) => c.recruiterName === name);
      return {
        name,
        submissions: theirs.length,
        selected: theirs.filter((c) => c.feedback === "SELECTED").length,
      };
    })
    .filter((e) => e.submissions > 0)
    .sort((a, b) => b.submissions - a.submissions)
    .slice(0, 8);

  const clearAll = () => {
    setCompanyFilter("");
    setRecruiterFilter("");
    setFrom("");
    setTo("");
  };

  const mode = dateMode(from, to, today);

  // Real, computed from the browser's actual current time — not hardcoded.
  const nowHour = new Date().getHours();
  const greeting =
    nowHour < 12
      ? "Good morning"
      : nowHour < 17
        ? "Good afternoon"
        : "Good evening";
  const todayLong = new Date().toLocaleDateString(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
  });

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: "100%", overflowX: "hidden" }}>
        <LoadingState variant="spinner" label="Loading dashboard…" />
      </Box>
    );
  }

  // ---- Shared freshness section (all roles) ----
  function FreshnessSection({ group, jobs: groupJobs }) {
    const info = FRESH_GROUPS.find((g) => g.key === group);

    // Empty group: still show the header so all three are always visible
    if (groupJobs.length === 0) {
      return (
        <Box sx={{ mb: 3 }}>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ mb: 1.5, px: 0.5 }}
          >
            <FiberManualRecordIcon
              sx={{ fontSize: 12, color: info.dotColor }}
            />
            <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
              {info.label} Positions
            </Typography>
            <Chip size="small" label="0" variant="outlined" />
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              {info.subtitle}
            </Typography>
          </Stack>
          <EmptyState
            title={`No ${info.label.toLowerCase()} positions awaiting candidates`}
          />
        </Box>
      );
    }

    return (
      <Box sx={{ mb: 3 }}>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ mb: 1.5, px: 0.5 }}
        >
          <FiberManualRecordIcon sx={{ fontSize: 12, color: info.dotColor }} />
          <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
            {info.label} Positions
          </Typography>
          <Chip size="small" label={`${groupJobs.length}`} variant="outlined" />
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            {info.subtitle}
          </Typography>
        </Stack>

        <Paper sx={{ overflow: "auto", maxHeight: 340 }}>
          <Table stickyHeader sx={{ minWidth: { xs: 520, md: 700 } }}>
            <TableHead>
              <TableRow sx={{ bgcolor: "background.default" }}>
                <TableCell sx={{ width: 40, bgcolor: "background.default" }} />
                <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: 12,
                    bgcolor: "background.default",
                  }}
                >
                  Job Code
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: 12,
                    bgcolor: "background.default",
                  }}
                >
                  Role
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: 12,
                    bgcolor: "background.default",
                  }}
                >
                  Company
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: 12,
                    bgcolor: "background.default",
                  }}
                >
                  SPOC
                </TableCell>
                {fullAccess && (
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: 12,
                      bgcolor: "background.default",
                    }}
                  >
                    Recruiters
                  </TableCell>
                )}
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 700,
                    fontSize: 12,
                    bgcolor: "background.default",
                  }}
                >
                  {fullAccess ? "Candidates" : "My Candidates"}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupJobs.map((j) => {
                const list = candsByJob[j.jobCode] || [];
                const recs = recruitersFor(j.jobCode);
                const isOpen = openPos === j.jobCode;
                return (
                  <Fragment key={j.jobCode}>
                    <TableRow
                      hover
                      sx={{
                        cursor: "pointer",
                        "& > *": { borderBottom: "unset" },
                      }}
                      onClick={() => setOpenPos(isOpen ? null : j.jobCode)}
                    >
                      <TableCell>
                        <KeyboardArrowRightIcon
                          fontSize="small"
                          sx={{
                            transition: "transform .2s",
                            transform: isOpen ? "rotate(90deg)" : "none",
                            color: "text.secondary",
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{ fontFamily: "monospace", fontWeight: 700 }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          <FiberManualRecordIcon
                            sx={{ fontSize: 10, color: info.dotColor }}
                          />
                          {j.jobCode}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {j.roleName || "—"}
                      </TableCell>
                      <TableCell>{j.companyName || "—"}</TableCell>
                      <TableCell>{j.spocName || "—"}</TableCell>
                      {fullAccess && (
                        <TableCell sx={{ maxWidth: 200 }}>
                          {recs.length === 0 ? (
                            "—"
                          ) : (
                            <Stack
                              direction="row"
                              spacing={0.5}
                              flexWrap="wrap"
                              useFlexGap
                            >
                              {recs.slice(0, 2).map((n) => (
                                <Chip
                                  key={n}
                                  size="small"
                                  label={n}
                                  variant="outlined"
                                />
                              ))}
                              {recs.length > 2 && (
                                <Tooltip title={recs.slice(2).join(", ")}>
                                  <Chip
                                    size="small"
                                    label={`+${recs.length - 2}`}
                                    variant="outlined"
                                  />
                                </Tooltip>
                              )}
                            </Stack>
                          )}
                        </TableCell>
                      )}
                      <TableCell align="center" sx={{ fontWeight: 700 }}>
                        {list.length}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        sx={{
                          py: 0,
                          borderBottom: isOpen ? undefined : "none",
                        }}
                        colSpan={fullAccess ? 7 : 6}
                      >
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <Box
                            sx={{
                              m: 1,
                              ml: { xs: 1, md: 5 },
                              p: 2,
                              bgcolor: "background.default",
                              borderRadius: 1.5,
                            }}
                          >
                            <Typography
                              sx={{ fontWeight: 700, fontSize: 13, mb: 1.5 }}
                            >
                              {fullAccess
                                ? `${list.length} candidate${list.length !== 1 ? "s" : ""} from ${recs.length} recruiter${recs.length !== 1 ? "s" : ""}`
                                : `Your ${list.length} candidate${list.length !== 1 ? "s" : ""} for this position`}
                            </Typography>
                            {list.length === 0 ? (
                              <Typography
                                sx={{ color: "text.secondary", fontSize: 13 }}
                              >
                                {fullAccess
                                  ? "No candidates submitted yet."
                                  : "You haven't submitted any candidates here yet."}
                              </Typography>
                            ) : (
                              <Box sx={{ overflowX: "auto" }}>
                                <Table size="small" sx={{ minWidth: 480 }}>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Candidate</TableCell>
                                      {fullAccess && (
                                        <TableCell>Recruiter</TableCell>
                                      )}
                                      <TableCell>Current Company</TableCell>
                                      <TableCell>Experience</TableCell>
                                      <TableCell>Status</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {list.map((c) => {
                                      const st = statusOf(c.feedback);
                                      return (
                                        <TableRow key={c.id}>
                                          <TableCell sx={{ fontWeight: 600 }}>
                                            {candidateName(c)}
                                          </TableCell>
                                          {fullAccess && (
                                            <TableCell>
                                              {c.recruiterName || "—"}
                                            </TableCell>
                                          )}
                                          <TableCell>
                                            {c.companyName || "—"}
                                          </TableCell>
                                          <TableCell>
                                            {c.totalExperience ||
                                              c.experienceYears ||
                                              "—"}
                                          </TableCell>
                                          <TableCell>
                                            <StatusBadge
                                              label={st.label}
                                              tone={
                                                MUI_COLOR_TO_TONE[st.color] ||
                                                "neutral"
                                              }
                                            />
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      </Box>
    );
  }

  return (
    <PageContainer sx={{ px: { xs: 2, md: 2.5 }, py: { xs: 2, md: 2 } }}>
      {/* Compact date controls (title now lives in the top bar) */}
      <Box sx={{ mb: 0.5 }}>
        <Typography sx={{ fontSize: 20, fontWeight: 800, color: NAVY }}>
          {greeting}, {(user.name || "there").split(" ")[0]}
        </Typography>
      </Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ sm: "center" }}
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 2, pl: { xs: 6, md: 0 } }}
      >
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            {todayLong}
            {attentionItems.length > 0 &&
              ` · ${attentionItems.length} thing${attentionItems.length !== 1 ? "s" : ""} need${attentionItems.length === 1 ? "s" : ""} you today`}
            {fullAccess
              ? " · data across all recruiters"
              : ` · ${user.name || ""}`}
          </Typography>
        </Box>
        <DateFilter
          from={from}
          to={to}
          onFromChange={setFrom}
          onToChange={setTo}
          isToday={mode === "today"}
          isAllTime={mode === "alltime"}
          onToday={() => {
            setFrom(today);
            setTo(today);
          }}
          onAllTime={() => {
            setFrom("");
            setTo("");
          }}
        />
        <RefreshButton onClick={() => loadData(true)} />
      </Stack>

      {/* Filters */}
      <FilterBar
        activeCount={
          [companyFilter, recruiterFilter, from, to].filter(Boolean).length
        }
        onClear={clearAll}
      >
        <TextField
          select
          label="Company"
          value={companyFilter}
          onChange={(e) => setCompanyFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 180, flex: 1 }}
        >
          <MenuItem value="">All Companies</MenuItem>
          {companyNames.map((n) => (
            <MenuItem key={n} value={n}>
              {n}
            </MenuItem>
          ))}
        </TextField>
        {fullAccess && (
          <TextField
            select
            label="Recruiter"
            value={recruiterFilter}
            onChange={(e) => setRecruiterFilter(e.target.value)}
            size="small"
            sx={{ minWidth: 180, flex: 1 }}
          >
            <MenuItem value="">All Recruiters</MenuItem>
            {recruiterNames.map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </TextField>
        )}
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {companyFilter || recruiterFilter
            ? "Filtered"
            : from || to
              ? "Date filtered"
              : "Showing all data"}
        </Typography>
      </FilterBar>

      {/* ══════════════════════════════════════════════════════════════
          Phase 6 (Design Gap Audit item F) — prototype-style section.
          Everything below uses the same filtered/derived data as the
          existing sections further down; nothing here is a new API call
          or fabricated number. The detailed per-status KPI grids and
          charts further below are intentionally kept as-is (not deleted)
          so no existing information is lost — this section is additive,
          matching the prototype's information architecture at the top of
          the page while preserving every existing view underneath.
          ══════════════════════════════════════════════════════════════ */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {headlineKpis.map((k) => (
          <Grid item xs={6} md={3} key={k.label}>
            <KpiCard
              value={k.value}
              label={k.label}
              color={k.color}
              alert={k.alert}
              actionLabel={k.actionLabel}
              onAction={k.onAction}
            />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1.5 }}>
              Candidate funnel
            </Typography>
            <CandidateFunnel stages={funnelStages} />
            {funnelLeak && (
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.25,
                  bgcolor: "#fffbeb",
                  border: "1px solid #fde68a",
                  borderRadius: "8px",
                }}
              >
                <Typography sx={{ fontSize: 12.5, color: "#92400e" }}>
                  Biggest leak:{" "}
                  <strong>
                    {funnelLeak.from} → {funnelLeak.to}
                  </strong>{" "}
                  converts at only {funnelLeak.pct}%
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1.5 }}>
              Ageing of open roles
            </Typography>
            <AgingHistogram
              buckets={agingBuckets}
              medianDays={medianAge}
              oldestDays={oldestAge}
            />
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1.5 }}>
              Needs attention
            </Typography>
            <AttentionQueue items={attentionItems} />
          </Paper>
        </Grid>
        {fullAccess && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: "100%" }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1.5 }}>
                Recruiter leaderboard
              </Typography>
              <RecruiterLeaderboard entries={leaderboardEntries} />
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* ── POSITIONS section (existing detailed breakdown, unchanged) ── */}
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 800,
          color: "text.secondary",
          textTransform: "uppercase",
          letterSpacing: 0.8,
          mb: 1.5,
        }}
      >
        Positions
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {positionKpis.map((k) => (
          <Grid item xs={6} sm={4} md={3} key={k.label}>
            <Card
              sx={{
                height: "100%",
                borderLeft: `4px solid ${k.color}`,
                "&:hover": {
                  boxShadow: "0 4px 16px rgba(15,27,51,0.10)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              <CardContent
                sx={{
                  p: { xs: 1.5, md: 2 },
                  "&:last-child": { pb: { xs: 1.5, md: 2 } },
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: `${k.color}14`,
                      color: k.color,
                    }}
                  >
                    <k.Icon fontSize="small" />
                  </Box>
                  <Chip
                    size="small"
                    label={k.tag}
                    sx={{
                      bgcolor: `${k.color}14`,
                      color: k.color,
                      fontWeight: 700,
                    }}
                  />
                </Stack>
                <Typography
                  sx={{
                    fontSize: { xs: 24, md: 30 },
                    fontWeight: 800,
                    mt: 1.5,
                    lineHeight: 1,
                  }}
                >
                  {k.value}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: "text.secondary",
                    mt: 0.5,
                    fontWeight: 600,
                  }}
                >
                  {k.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ── CANDIDATES section ── */}
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 800,
          color: "text.secondary",
          textTransform: "uppercase",
          letterSpacing: 0.8,
          mb: 1.5,
        }}
      >
        Candidates
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {candidateKpis.map((k) => (
          <Grid item xs={6} sm={4} md={3} key={k.label}>
            <Card
              sx={{
                height: "100%",
                borderLeft: `4px solid ${k.color}`,
                "&:hover": {
                  boxShadow: "0 4px 16px rgba(15,27,51,0.10)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              <CardContent
                sx={{
                  p: { xs: 1.5, md: 2 },
                  "&:last-child": { pb: { xs: 1.5, md: 2 } },
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: `${k.color}14`,
                      color: k.color,
                    }}
                  >
                    <k.Icon fontSize="small" />
                  </Box>
                  <Chip
                    size="small"
                    label={k.tag}
                    sx={{
                      bgcolor: `${k.color}14`,
                      color: k.color,
                      fontWeight: 700,
                    }}
                  />
                </Stack>
                <Typography
                  sx={{
                    fontSize: { xs: 24, md: 30 },
                    fontWeight: 800,
                    mt: 1.5,
                    lineHeight: 1,
                  }}
                >
                  {k.value}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: "text.secondary",
                    mt: 0.5,
                    fontWeight: 600,
                  }}
                >
                  {k.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ── Charts ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1 }}>
              Candidates by Status
            </Typography>
            {candidateChartData.length === 0 ? (
              <EmptyState
                title="No candidate data to chart"
                description="Candidates matching the current filters will appear here once added."
              />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={candidateChartData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                  >
                    {candidateChartData.map((d) => (
                      <Cell key={d.name} fill={d.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                  <Legend
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{ fontSize: 12 }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1 }}>
              Positions by Status
            </Typography>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={positionChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <RechartsTooltip />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {positionChartData.map((d) => (
                    <Cell key={d.name} fill={d.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 1.5, mb: 3 }}>
        <Stack
          direction="row"
          spacing={2}
          flexWrap="wrap"
          useFlexGap
          alignItems="center"
        >
          <Typography
            sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary" }}
          >
            Position freshness:
          </Typography>
          {FRESH_LEGEND.map((d) => (
            <Stack key={d.l} direction="row" spacing={0.75} alignItems="center">
              <Box
                sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: d.c }}
              />
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                {d.l}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Paper>

      {/* Positions still AWAITING candidates, grouped by freshness (Old → Aging → Fresh) */}
      <Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h6">Positions Awaiting Candidates</Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              Positions with no candidates submitted yet — oldest first
            </Typography>
          </Box>
          <Chip label={`${awaitingCount} awaiting`} variant="outlined" />
        </Stack>

        {jobsByFreshness.fresh.length === 0 ? (
          <Paper sx={{ py: 2 }}>
            <EmptyState
              icon={<WorkOutlineIcon sx={{ fontSize: 28, color: ACCENT }} />}
              title="No fresh positions awaiting candidates"
              description={
                companyFilter || recruiterFilter || from || to
                  ? "No fresh (≤1 day old) positions are awaiting candidates for the current filters."
                  : "Positions opened within the last day with no candidates yet will appear here."
              }
            />
          </Paper>
        ) : (
          <FreshnessSection group="fresh" jobs={jobsByFreshness.fresh} />
        )}
      </Box>
    </PageContainer>
  );
}
